import React, { useCallback, useMemo } from "react";
import { TaskTypesType } from "../../types/TaskUIType";
import { TaskScreenComponent } from "./TaskScreenComponent/TaskScreenComponent";

import { Connection, Task, TaskCheck, TaskNumber } from "./State/Routine/types";
import { RoutineEditScreenProps } from "./RoutineEditMain/RoutineEditNavigation";
import { graphql, useLazyLoadQuery } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { RoutineEditChecklistTaskQuery } from "./__generated__/RoutineEditChecklistTaskQuery.graphql";
import {
  useRoutineEditState,
  useRoutineEditStateSetParams,
} from "./State/RoutineEditState";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";

const getNewTask = (
  task: Task,
  type: "TaskCheck" | "TaskNumber" | "TaskText"
) => {
  const base = {
    id: task.id,
    title: task.title,
    instructions: task.instructions,
    images: task.images,
    type,
  };
  if (type === "TaskCheck") {
    return {
      ...base,
      type: "TaskCheck" as const,
      sentiments: ["POSITIVE" as const],
    } as Task;
  } else if (type === "TaskNumber") {
    return {
      ...base,
      type: "TaskNumber" as const,
      unit: "CUSTOM" as const,
    } as Task;
  }
  return {
    ...base,
    type: "TaskText" as const,
  } as Task;
};

export function RoutineEditChecklistTask(
  props: RoutineEditScreenProps<"RoutineEditChecklistTask">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerRoutineEditChecklistTask {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerRoutineEditChecklistTask(
  props: RoutineEditScreenProps<"RoutineEditChecklistTask"> & {
    fetchKey: string;
  }
) {
  const setParams = useRoutineEditStateSetParams();
  const { tasks = [], connections = [], sections = [] } = useRoutineEditState();

  const routineId = props.route.params.routineId;

  const data = useLazyLoadQuery<RoutineEditChecklistTaskQuery>(
    graphql`
      query RoutineEditChecklistTaskQuery(
        $id: ID!
        $workspaceId: ID!
        $include: Boolean!
        $includeWorkspace: Boolean!
      ) {
        workspace(id: $workspaceId) @include(if: $includeWorkspace) {
          topics {
            id
            ...TaskScreenComponent_topics
          }
        }

        routine(id: $id) @include(if: $include) {
          id
          isOrg
          activeVersion {
            tasks {
              id
            }
          }
        }
      }
    `,
    {
      id: routineId ?? "",
      include: !!routineId,
      workspaceId: props.route.params.orgWorkspaceId ?? "",
      includeWorkspace: !!props.route.params.orgWorkspaceId,
    },
    {
      fetchKey: props.fetchKey,
    }
  );

  const task = useMemo(
    () => tasks.find(({ id }) => id === props.route.params.taskId),
    [tasks, props.route.params.taskId]
  );

  const hasRemote = useMemo(
    () =>
      data.routine?.activeVersion.tasks.some(({ id }) => id === task?.id) ??
      false,
    [data.routine?.activeVersion.tasks, task?.id]
  );

  const setType = useCallback(
    (type: TaskTypesType) => {
      if (task) {
        setParams({
          tasks: tasks.map((_task) =>
            _task.id === task.id ? getNewTask(task, type) : _task
          ),
        });
      }
    },
    [setParams, task, tasks]
  );

  const setConnections = useCallback(
    (connections: Connection[]) => setParams({ connections }),
    [setParams]
  );

  const taskUpdateGoal = useCallback(
    (taskId: string, goal: TaskNumber["goal"]) => {
      setParams({
        tasks: tasks.map((_task) =>
          _task.id === taskId && _task.type === "TaskNumber"
            ? { ..._task, goal }
            : _task
        ),
      });
    },
    [setParams, tasks]
  );
  const taskUpdateUnit = useCallback(
    (taskId: string, unit: TaskNumber["unit"]) => {
      setParams({
        tasks: tasks.map((_task) =>
          _task.id === taskId && _task.type === "TaskNumber"
            ? { ..._task, unit }
            : _task
        ),
      });
    },
    [setParams, tasks]
  );
  const taskUpdateSentiments = useCallback(
    (taskId: string, sentiments: TaskCheck["sentiments"]) => {
      setParams({
        tasks: tasks.map((_task) =>
          _task.id === taskId && _task.type === "TaskCheck"
            ? { ..._task, sentiments }
            : _task
        ),
      });
    },
    [setParams, tasks]
  );
  useNavigationHeader({
    type: "DONE",
    onDone: () => {
      props.navigation.goBack();
    },
    defaultContentStyle: true,
    title: task?.title ?? "",
  });

  if (!task) {
    return null;
  }

  return (
    <TaskScreenComponent
      isOrg={data.routine?.isOrg ?? false}
      hasRemote={hasRemote}
      connections={connections}
      sections={Object.values(sections)}
      setConnections={setConnections}
      task={task}
      setType={setType}
      taskUpdateGoal={taskUpdateGoal}
      taskUpdateUnit={taskUpdateUnit}
      taskUpdateSentiments={taskUpdateSentiments}
      topics={data.workspace?.topics ?? null}
    />
  );
}
